import { MEDIA_QUERY_STATES } from '@constants/media-query';
import { useAppDispatch } from '@redux/hooks';
import * as VendorImpressionThunks from '@redux/vendorImpressions/thunks';
import { useMedia } from '@tkww/orion-web-app-kernel';
import React, { useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { CategoryRecommendationsContext } from '../CategoryRecommendationsProvider';
import Styles from '../McVrmLiteWidget.scss';
import { NoResults } from './NoResults';
import { TrackableVendorCard } from './TrackableVendorCard';
import { VendorListSkeleton } from './VendorListSkeleton';

export const VendorList = () => {
	const {
		selectedCategoryCode,
		isLoading,
		shouldShowNoResults,
		selectedCategoryVendors,
	} = useContext(CategoryRecommendationsContext);
	const dispatch = useAppDispatch();
	const trackedCategoryCodesRef = useRef<Set<string>>(new Set());
	const lastLoadedVendorsCountRef = useRef(3);
	const vendorListRef = useRef<HTMLDivElement>(null);
	const lastLoadedVendorsHeightRef = useRef<number>();

	const shouldTrackSelectedCategory =
		selectedCategoryCode !== null &&
		!trackedCategoryCodesRef.current.has(selectedCategoryCode);

	const isDesktop = useMedia('@lg') === MEDIA_QUERY_STATES.matched;
	const isMd = useMedia('@md') === MEDIA_QUERY_STATES.matched;
	const isTabletSize = isMd && !isDesktop;

	useEffect(() => {
		if (
			!isLoading &&
			selectedCategoryVendors.length > 0 &&
			shouldTrackSelectedCategory
		) {
			dispatch(
				VendorImpressionThunks.transmitImpressions({
					transmitMinimumLimit: 1,
				}),
			);
			trackedCategoryCodesRef.current.add(selectedCategoryCode);
		}
	}, [
		dispatch,
		isLoading,
		selectedCategoryVendors,
		selectedCategoryCode,
		shouldTrackSelectedCategory,
	]);

	useEffect(() => {
		if (!isLoading && selectedCategoryVendors.length > 0) {
			lastLoadedVendorsCountRef.current = selectedCategoryVendors.length;
		}
	}, [isLoading, selectedCategoryVendors]);

	useLayoutEffect(() => {
		if (
			!isLoading &&
			selectedCategoryVendors.length > 0 &&
			vendorListRef.current
		) {
			const frameId = requestAnimationFrame(() => {
				if (!vendorListRef.current) return;

				const height = vendorListRef.current.getBoundingClientRect().height;
				if (height > 0) {
					lastLoadedVendorsHeightRef.current = height;
				}
			});

			return () => cancelAnimationFrame(frameId);
		}
	}, [isLoading, selectedCategoryVendors.length]);

	if (isLoading) {
		const skeletonItems =
			lastLoadedVendorsCountRef.current > 0
				? lastLoadedVendorsCountRef.current
				: 3;

		return (
			<VendorListSkeleton
				items={skeletonItems}
				height={lastLoadedVendorsHeightRef.current}
			/>
		);
	}

	if (shouldShowNoResults) {
		return (
			<div
				className={Styles.noResultsContainer}
				style={{ height: lastLoadedVendorsHeightRef.current }}
			>
				<NoResults />
			</div>
		);
	}

	return (
		<div ref={vendorListRef} className={Styles.cardsContainer}>
			{selectedCategoryVendors.map((vendor, index) => (
				<TrackableVendorCard
					key={vendor.id}
					vendor={vendor}
					index={index}
					shouldTrack={shouldTrackSelectedCategory}
					centerFavoriteSnackbar={isTabletSize && (index === 0 || index === 1)}
				/>
			))}
		</div>
	);
};
